import React, { type ComponentProps } from 'react';

import { reportBudget } from 'loot-core/client/queries';
import { type ApplyBudgetTemplateMode } from 'loot-core/server/budget-templates/types/template-apply-mode';
import { sheetForYear } from 'loot-core/shared/months';
import { type BudgetTemplateLine } from 'loot-core/src/shared/budget-template';
import { type CategoryEntity } from 'loot-core/src/types/models';

import { theme } from '../../style';
import { SidebarCategory } from '../budget/SidebarCategory';
import { View } from '../common/View';
import {
  type DragState,
  DropHighlight,
  type OnDragChangeCallback,
  type OnDropCallback,
  useDraggable,
  useDroppable,
} from '../sort';
import { CellValue } from '../spreadsheet/CellValue';
import { useFormat } from '../spreadsheet/useFormat';
import { Row } from '../table';

import BudgetTemplateControls from './BudgetTemplateControls';
import MonthCell from './MonthCell';
import { type CellEditing } from './types';
import { VIEW_MODE } from './util';

type IncomeCategoryProps = {
  year: number;
  cat: CategoryEntity;
  categoryType: string;
  viewMode: VIEW_MODE;
  budgetLine: Partial<BudgetTemplateLine>;
  months: { index: number; month: string }[];
  isLast?: boolean;
  editingCell: CellEditing;
  dragState: DragState<CategoryEntity>;
  onEditCell: (cell: CellEditing) => void;
  onEditName: ComponentProps<typeof SidebarCategory>['onEditName'];
  onSave: ComponentProps<typeof SidebarCategory>['onSave'];
  onDelete: ComponentProps<typeof SidebarCategory>['onDelete'];
  onBudgetLineChange: (budgetLine: Partial<BudgetTemplateLine>) => void;
  onBudgetLineApply: (
    budgetLine: Partial<BudgetTemplateLine>,
    mode: ApplyBudgetTemplateMode,
  ) => void;
  onDragChange: OnDragChangeCallback<CategoryEntity>;
  onReorder: OnDropCallback;
  onBudgetAction: (month: string, action: string, arg: unknown) => void;
};

function Category({
  year,
  cat,
  categoryType,
  viewMode,
  budgetLine,
  months,
  isLast,
  editingCell,
  dragState,
  onEditCell,
  onEditName,
  onSave,
  onDelete,
  onBudgetLineChange,
  onBudgetLineApply,
  onDragChange,
  onReorder,
  onBudgetAction,
}: IncomeCategoryProps) {
  const format = useFormat();
  let dragging = dragState && dragState.item === cat;

  if (dragState && dragState.item.id === cat.cat_group) {
    dragging = true;
  }

  const dragType = cat.is_income ? 'income-category' : 'expense-category';
  const { dragRef } = useDraggable({
    type: dragType,
    onDragChange,
    item: cat,
    canDrag: editingCell === null,
  });
  const { dropRef, dropPos } = useDroppable({
    types: dragType,
    id: cat.id,
    onDrop: onReorder,
  });

  return (
    <Row
      data-adoption-id={'category-' + categoryType}
      innerRef={dropRef}
      collapsed={true}
      style={{
        backgroundColor: theme.tableBackground,
        opacity: cat.hidden ? 0.5 : undefined,
        minWidth: 'fit-content',
      }}
    >
      <DropHighlight pos={dropPos} offset={{ top: 1 }} />

      <SidebarCategory
        innerRef={dragRef}
        category={cat}
        dragPreview={dragging && dragState.preview}
        dragging={dragging && !dragState.preview}
        isLast={isLast}
        editing={
          editingCell &&
          editingCell.column === 'name' &&
          editingCell.id === cat.id
        }
        onEditName={onEditName}
        onSave={onSave}
        onDelete={onDelete}
        style={{
          position: 'sticky',
          left: 0,
          backgroundColor: theme.gsGreen200,
          zIndex: 1000,
        }}
      />
      <View
        data-adoption-id="planning-budget-controls"
        style={{
          borderLeft: '1px solid ' + theme.tableBorder,
          position: 'sticky',
          left: '200px',
          backgroundColor: '#FFF',
          ...(viewMode === VIEW_MODE.COMBINED
            ? {
                borderRight: '1px solid ' + theme.tableBorder,
                backgroundColor: theme.gsGreen100,
              }
            : {}),
          zIndex: 1000,
        }}
      >
        <BudgetTemplateControls
          year={year.toString()}
          editingCell={editingCell}
          viewMode={viewMode}
          budgetLine={budgetLine}
          months={months}
          onEditCell={onEditCell}
          onBudgetLineChange={onBudgetLineChange}
          onBudgetLineApply={onBudgetLineApply}
          onBudgetAction={onBudgetAction}
        />
      </View>
      {/*{viewMode === VIEW_MODE.ONLY_BUDGET ? null : (*/}
      <View
        style={{
          minWidth: 'fit-content',
          flex: 1,
          flexDirection: 'row',
          alignItems: 'stretch',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          opacity: cat.hidden ? 0.33 : undefined,
        }}
      >
        <View
          style={{
            minWidth: '100px',
            flex: 1,
            textAlign: 'right',
            justifyContent: 'center',
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: theme.tableBorder,
          }}
        >
          <View
            style={{
              padding: '0 5px',
            }}
          >
            <CellValue
              binding={
                sheetForYear(year + '') +
                '!' +
                reportBudget.catYearMonthlyAverage(cat.id)
              }
              type="financial"
              style={{ color: theme.pageTextLight }}
              formatter={value => {
                return format(parseInt(value || '0'), 'financial');
              }}
            />
          </View>
        </View>
        <View
          style={{
            minWidth: '100px',
            flex: 1,
            textAlign: 'right',
            justifyContent: 'center',
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: theme.tableBorder,
          }}
        >
          <View
            style={{
              padding: '0 5px',
            }}
          >
            <CellValue
              binding={
                sheetForYear(year + '') + '!' + reportBudget.catBudgeted(cat.id)
              }
              type="financial"
              style={{ color: theme.pageTextLight }}
              formatter={value => {
                return format(parseFloat(value || '0'), 'financial');
              }}
            />
          </View>
        </View>
        {months.map(o => (
          <MonthCell
            key={o.index}
            category={cat}
            month={o.month}
            monthIndex={o.index}
            editing={
              editingCell &&
              editingCell.column === 'month' &&
              editingCell.id === cat.id &&
              editingCell.monthIndex === o.index
            }
            onEditCell={onEditCell}
            onBudgetAction={onBudgetAction}
          />
        ))}
      </View>
      {/*)}*/}
    </Row>
  );
}

export default Category;
